import { GetStaticPaths, GetStaticPropsContext } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import styled from 'styled-components';
import {
  getFilterOptions,
  getFooterLinks,
  getNews
} from '../../../new-lib/apis';
import NewsCard from '../../../new-components/cards/NewsCard';
import ImageWrapper from '../../../new-components/ImageWrapper';
import {
  setExperimentVariant,
  setIsMobile
} from '../../../redux/actions/appActions';
import { wrapper } from '../../../redux/store';
import { StaticPagesUtility } from '../../../new-lib/utilities/static-pages.utility';
import { SCHEMA_TYPES } from '../../../features/structured-data/components/SchemaBuilder';
import Image from 'next/image';
import WebAppJourney from '../../../features/home/components/web-app-journey/WebAppJourney';
import ContactFormContainer from '../../../features/contact/components/organic-form/ContactFormContainer';
import { FormTypes } from '../../../new-lib/constants/common';
import { useSelector } from 'react-redux';

const AboutUs = (props: any) => {
  const { t } = useTranslation('about-us');
  const isMobile = useSelector((state: any) => state.app.isMobile);
  const boardMembers: { image: string; name: string; title: string }[] = [
    {
      image: 'beltagy.png',
      name: 'mostafa_el_beltagy',
      title: 'Co-founder & CEO'
    },
    {
      image: 'abdelazim.png',
      name: 'abdelazim_osman',
      title: 'Co-founder & CMO'
    },
    {
      image: 'ahmed-rafea.png',
      name: 'ahmed_rafea',
      title: 'Co-founder & CBDO'
    },
    {
      image: 'aly-rafea.png',
      name: 'aly_rafea',
      title: 'Co-founder & CCO'
    },
    {
      image: 'abou-ghanima.png',
      name: 'mohamed_abou_ghanima',
      title: 'Co-founder & COO'
    }
  ];

  const ourProducts = [
    {
      image: 'nawy-shares.png',
      alt: 'Nawy Shares'
    },
    {
      image: 'nawy-now.png',
      alt: 'Nawy Now'
    },
    {
      image: 'nawy-unlocked.png',
      alt: 'Nawy Unlocked'
    },
    {
      image: 'nawy-partners.png',
      alt: 'Nawy Partners'
    }
  ];

  const FormHeader = () => {
    const { t } = useTranslation('contact');

    return (
      <FormHeaderContainer>
        <h2 className="form-title">{t('need_expert_advice')}</h2>
        <p className="form-subtitle">{t('fill_out_the_form')}</p>
      </FormHeaderContainer>
    );
  };

  const BoardMembers = () => {
    if (isMobile) {
      return (
        <div className="board-members">
          <BoardMemberImageContainer isMobile={isMobile}>
            <ImageWrapper
              src={`/assets/images/about-images/beltagy.png`}
              alt={t('mostafa_el_beltagy')}
              fill
              style={{ objectFit: 'contain' }}
            />
            <p>{t('mostafa_el_beltagy')}</p>
            <span>Co-founder & CEO</span>
          </BoardMemberImageContainer>

          <div className="board-members-row">
            <BoardMemberImageContainer isMobile={isMobile}>
              <ImageWrapper
                src={`/assets/images/about-images/abdelazim.png`}
                alt={t('abdelazim_osman')}
                fill
                style={{ objectFit: 'contain' }}
              />
              <p>{t('abdelazim_osman')}</p>
              <span>Co-founder & CMO</span>
            </BoardMemberImageContainer>

            <BoardMemberImageContainer isMobile={isMobile}>
              <ImageWrapper
                src={`/assets/images/about-images/ahmed-rafea.png`}
                alt={t('ahmed_rafea')}
                fill
                style={{ objectFit: 'contain' }}
              />
              <p>{t('ahmed_rafea')}</p>
              <span>Co-founder & CBDO</span>
            </BoardMemberImageContainer>
          </div>
          <div className="board-members-row">
            <BoardMemberImageContainer isMobile={isMobile}>
              <ImageWrapper
                src={`/assets/images/about-images/aly-rafea.png`}
                alt={t('aly_rafea')}
                fill
                style={{ objectFit: 'contain' }}
              />
              <p>{t('aly_rafea')}</p>
              <span>Co-founder & CCO</span>
            </BoardMemberImageContainer>

            <BoardMemberImageContainer isMobile={isMobile}>
              <ImageWrapper
                src={`/assets/images/about-images/abou-ghanima.png`}
                alt={t('mohamed_abou_ghanima')}
                fill
                style={{ objectFit: 'contain' }}
              />
              <p>{t('mohamed_abou_ghanima')}</p>
              <span>Co-founder & COO</span>
            </BoardMemberImageContainer>
          </div>
        </div>
      );
    } else
      return (
        <div className="board-members">
          {boardMembers.map(member => (
            <BoardMemberImageContainer isMobile={isMobile} key={member.image}>
              <ImageWrapper
                src={`/assets/images/about-images/${member.image}`}
                alt={member.name}
                fill
                style={{ objectFit: 'contain' }}
              />
              <p>{t(member.name)}</p>
              <span>{member.title}</span>
            </BoardMemberImageContainer>
          ))}
        </div>
      );
  };

  return (
    <MainContainer>
      <BannerContainer isMobile={isMobile}>
        <ImageWrapper
          src={'/assets/images/about-images/about-us-banner.png'}
          fill
          style={{ objectFit: 'cover', aspectRatio: 'unset' }}
          alt="AboutUsBanner"
        />
        <div className="container-fluid banner-content">
          <h1>{t('about_us')}</h1>
        </div>
      </BannerContainer>
      <Container isMobile={isMobile}>
        <div className="container-fluid get-to-know-section">
          <div className="text-section">
            <div>
              <h3>{t('get_to_know')}</h3>
              <h2>{t('where_innovation_meets_expertise')}</h2>
            </div>
            <p className="body">{t('about_body1')}</p>
            <div>
              <p className="body">{t('about_body2')}</p>
              <p className="body">{t('about_body3')}</p>
            </div>

            {!isMobile && (
              <div className="our-vision-section">
                <div className="our-vision-logo-section">
                  <Image
                    height={isMobile ? 40 : 49}
                    width={isMobile ? 40 : 49}
                    alt="Vision"
                    src={`/assets/icons/about-us/our-vision-logo.svg`}
                  />
                  <span>{t('our_vision')}</span>
                </div>
                <p>{t('vision_body')}</p>
              </div>
            )}
          </div>
          <div className="image-section">
            <ImageWrapper
              src={'/assets/images/about-images/about-us-graphic.png'}
              width={isMobile ? 280 : 427}
              height={isMobile ? 320 : 450}
              style={{ objectFit: 'contain' }}
              alt="AboutUsGraphic"
            />
          </div>
          {isMobile && (
            <div className="our-vision-section">
              <div className="our-vision-logo-section">
                <Image
                  height={49}
                  width={49}
                  alt="Vision"
                  src={`/assets/icons/about-us/our-vision-logo.svg`}
                />
                <span>{t('our_vision')}</span>
              </div>
              <p>{t('vision_body')}</p>
            </div>
          )}
        </div>
        <div className="container-fluid">
          <div className="board-section">
            <div className="board-header-section">
              <h2>{t('who_are_we')}</h2>
              <h3>{t('who_are_we_subheader')}</h3>
            </div>

            <BoardMembers />
          </div>
        </div>

        <div className="container-fluid our-products-section">
          <div className="header-section">
            <h2>{t('our_products')}</h2>
            <p>{t('our_products_subheader')}</p>
          </div>
          <div className="products-images">
            {ourProducts.map(product => (
              <ProductImageContainer key={product.image}>
                <ImageWrapper
                  src={`/assets/images/about-images/${product.image}`}
                  alt={product.alt}
                  fill
                  style={{ objectFit: 'contain' }}
                />
              </ProductImageContainer>
            ))}
          </div>
        </div>

        <div className="container-fluid news-container">
          <h3>{t('latest_news')}</h3>
          <div className="news-cards hide-scrollbar">
            {props.news.map((newsItem: any) => {
              return <NewsCard {...newsItem} key={newsItem.id} />;
            })}
          </div>
        </div>
        <div className="container-fluid">
          <WebAppJourney />
        </div>
        <div className="container-fluid">
          <ContactFormContainer
            layout={{ header: <FormHeader /> }}
            renderedInputs={{
              name: true,
              message: true,
              mobileNumber: true,
              location: true
            }}
            formSource={FormTypes.ORGANIC_FORM}
            isAboutUsPage
          />
        </div>
      </Container>
    </MainContainer>
  );
};

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: auto;
`;

const BannerContainer = styled.div<any>`
  height: ${props => (props.isMobile ? '125px' : '250px')};
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: end;

  .banner-content {
    position: relative;
    z-index: 1;
    margin-bottom: ${props => (props.isMobile ? '16px' : '32px')};
  }

  h1 {
    font-family: materialSemiBold;
    font-size: 26px;
    color: #ffffff;
    line-height: 41.6px;
  }
`;

const Container = styled.div<any>`
  margin-top: 64px !important;
  margin-bottom: 64px !important;
  display: flex;
  flex-direction: column;
  gap: 64px;

  .banner-section {
    height: 224px;
    position: relative;
    flex-shrink: 0;

    h1 {
      position: absolute;
      font-family: materialBold;
      font-size: 26px;
      color: #ffffff;
      top: 70%;
      left: 80px;
      line-height: 41.6px;
    }
  }

  .get-to-know-section {
    width: 100%;
    display: flex;
    flex-direction: ${props => (props.isMobile ? 'column' : 'row')};
    justify-content: space-between;
    gap: ${props => (props.isMobile ? '40px' : '0px')};

    .text-section {
      display: flex;
      flex-direction: column;
      gap: 24px;
      max-width: 680px;

      h2 {
        color: #212431;
        font-size: ${props => (props.isMobile ? '22px' : '32px')};
        font-family: materialBold;
        line-height: 140%;
        text-transform: uppercase;
        width: 380px;
        word-break: break-word;
        white-space: normal;
      }

      h3 {
        color: #215a8e;
        font-size: 20px;
        font-family: materialMedium;
        line-height: 30px;
      }

      .body {
        color: #1a2028;
        font-size: 16px;
        font-family: materialLight;
        line-height: 24px;
      }
    }

    .our-vision-section {
      display: flex;
      align-items: flex-start;
      gap: 16px;
      align-self: stretch;
      padding-top: 16px;

      p {
        color: #1a2028;
        font-size: ${props => (props.isMobile ? '14px' : '18px')};
        font-family: materialMedium;
        line-height: 140%;
      }

      .our-vision-logo-section {
        display: flex;
        flex-direction: ${props => (props.isMobile ? 'column' : 'row')};
        align-items: flex-start;
        gap: ${props => (props.isMobile ? '4px' : ' 12px')};

        span {
          color: #215a8e;
          font-size: ${props => (props.isMobile ? '16px' : '18px')};
          font-family: materialSemiBold;
          line-height: 120%;
          width: ${props => (props.isMobile ? 'auto' : '63px')};
          word-break: ${props => (props.isMobile ? 'normal' : 'break-word')};
          white-space: ${props => (props.isMobile ? 'nowrap' : 'normal')};
        }
      }
    }
    .image-section {
      width: ${props => (props.isMobile ? ' 100%' : 'auto')};
      display: flex;
      justify-content: center;
    }
  }

  .board-section {
    background: #f8fafc;
    height: ${props => (props.isMobile ? '700px' : '341px')};
    position: relative;
    padding-top: ${props => (props.isMobile ? '24px' : '50px')};
    margin-bottom: ${props => (props.isMobile ? '55px' : '120px')};
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
    border-radius: 37px;

    .board-header-section {
      width: ${props => (props.isMobile ? '302px' : '614px')};
      display: flex;
      flex-direction: column;
      gap: ${props => (props.isMobile ? '4px' : '8px')};
      align-items: center;
      h2 {
        color: #212431;
        text-align: center;
        font-size: ${props => (props.isMobile ? '22px' : '32px')};
        font-family: MaterialBold;
        line-height: 140%;
      }

      h3 {
        color: #212431;
        text-align: center;
        font-size: ${props => (props.isMobile ? '14px' : '20px')};
        font-family: MaterialLight;
        line-height: 150%;
      }
    }

    .board-members {
      width: 100%;
      height: ${props => (props.isMobile ? 'auto' : '100px')};
      position: absolute;
      display: flex;
      flex-direction: ${props => (props.isMobile ? 'column' : 'row')};
      justify-content: center;
      align-items: center;
      gap: ${props => (props.isMobile ? '18px' : '24px')};
      top: ${props => (props.isMobile ? '140px' : '270px')};

      .board-members-row {
        display: flex;
        gap: 18px;
      }
    }
  }

  .our-products-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${props => (props.isMobile ? '24px' : '40px')};
    margin-top: ${props => (props.isMobile ? '60px' : '0px')} !important;

    .header-section {
      h2 {
        color: #212431;
        text-align: center;
        font-size: ${props => (props.isMobile ? '22px' : '30px')};
        font-family: materialBold;
        line-height: 140%;
      }

      p {
        color: #212431;
        text-align: center;
        font-size: ${props => (props.isMobile ? '14px' : '20px')};
        font-family: materialLight;
        line-height: 150%;
      }
    }

    .products-images {
      display: ${props => (props.isMobile ? 'grid' : 'flex')};
      grid-template-columns: ${props =>
        props.isMobile ? 'repeat(2, 1fr)' : 'none'};
      justify-content: center;
      align-items: center;
      gap: ${props => (props.isMobile ? '38px' : '80px')};
      align-self: stretch;
      ${props =>
        props.isMobile &&
        `
        width: 100%;
        margin: 0 auto;
        place-items: center;
      `}
    }
  }

  .news-container {
    h3 {
      color: #212431;
      font-size: ${props => (props.isMobile ? '22px' : '32px')};
      font-family: materialBold;
      line-height: 44.8px;
    }
  }
  .news-cards {
    width: 100%;
    padding: 4px 0px;
    display: flex;
    gap: 24px;
    margin-top: 24px;
    overflow-x: auto;
    div {
    }
  }

  @media screen and (max-width: ${props =>
      props.theme.breakPoints.tablet.max}) {
    .news-container {
      width: 100%;
      display: flex;
      flex-direction: column;
      h4 {
        color: #212431;
        font-family: materialBold;
        font-size: 22px;
        line-height: unset;
      }
      .news-cards {
        flex-direction: column;
      }
    }
  }
`;

const BoardMemberImageContainer = styled.div<any>`
  position: relative;
  width: ${props => (props.isMobile ? '171.5px' : '204px')};
  height: ${props => (props.isMobile ? '203px' : '241px')};

  p {
    position: absolute;
    bottom: 48px;
    width: 100%;
    left: 0;
    color: #fff;
    text-align: center;
    font-size: ${props => (props.isMobile ? '13px' : '16px')};
    font-family: materialSemiBold;
    line-height: 150%;
  }

  span {
    position: absolute;
    bottom: 29px;
    width: 100%;
    left: 0;
    color: #fff;
    text-align: center;
    font-size: ${props => (props.isMobile ? '11.5px' : '14px')};
    font-family: materialLight;
    line-height: 150%;
  }
`;

const ProductImageContainer = styled.div`
  position: relative;
  width: 110px;
  height: 56px;
`;

const FormHeaderContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  margin-bottom: 24px;

  .form-title {
    color: #212427;
    font-size: 24px;
    font-family: materialBold;
    text-transform: capitalize;
  }

  .form-subtitle {
    color: #212431;
    font-size: 18px;
    font-weight: materialLight;
    line-height: 27px;
  }
`;

export const getStaticProps = wrapper.getStaticProps(
  store =>
    async ({ params, locale }: GetStaticPropsContext) => {
      const [footerLinks, filterOptions, news] = await Promise.all([
        getFooterLinks(locale, 'about'),
        getFilterOptions(locale),
        getNews(locale)
      ]);

      store.dispatch(setIsMobile(params?.device === 'mobile'));
      store.dispatch(setExperimentVariant(params?.variant as string));

      return {
        revalidate: 60 * 60 * 24,
        props: {
          ...(await serverSideTranslations(locale as string, [
            'about-us',
            'common',
            'contact',
            'search',
            'home'
          ])),
          filterOptions: JSON.stringify(filterOptions) || '',
          layout: {
            footerLinks,
            url: `${process.env.NEXT_PUBLIC_HOST}/about-us`
          },
          schemas: [SCHEMA_TYPES.organization],
          news: news
        }
      };
    }
);

export const getStaticPaths = (async () => {
  return { paths: StaticPagesUtility.get(), fallback: false };
}) satisfies GetStaticPaths;

export default AboutUs;
